import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, ArrowRight, ArrowLeft, Lock, Tag, X, Check } from 'lucide-react';
import { apiClient, publicApi, useAuth, formatApiError } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { cartStorage } from '../utils/cartStorage';
import { useLanguage } from '../context/LanguageContext';

export default function Cart() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    try { return localStorage.getItem('joey_shipping_country') || 'France'; } catch (e) { return 'France'; }
  });
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  
  const { isAuthenticated, isPro } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    try { localStorage.setItem('joey_shipping_country', selectedCountry || ''); } catch (e) {}
    if (!selectedCountry || selectedCountry === '__other__') {
      setShipping(null);
      return;
    }
    // Reset shipping to first method valid for this country when current selection isn't valid
    const valid = shippingMethods.filter(m => Array.isArray(m.countries) && m.countries.some(c => c.toLowerCase() === selectedCountry.toLowerCase()));
    if (valid.length === 0) {
      setShipping(null);
    } else if (!valid.some(m => m.id === shipping)) {
      setShipping(valid[0].id);
    }
  }, [selectedCountry, shippingMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCart = async () => {
    try {
      if (isAuthenticated) {
        // Utilisateur connecté : charger depuis l'API
        const [cartRes, methodsRes, productsRes] = await Promise.all([
          apiClient.get('/cart'),
          apiClient.get('/shipping-methods'),
          publicApi.get('/products?admin=true') // Récupérer les produits originaux pour matcher les IDs du panier
        ]);
        setItems(cartRes.data);
        setProducts(productsRes.data);
        setShippingMethods(methodsRes.data);
        if (methodsRes.data.length > 0) {
          setShipping(methodsRes.data[0].id);
        }
      } else {
        // Visiteur non connecté : charger depuis localStorage
        const localItems = cartStorage.getItems();
        setItems(localItems);
        
        const [methodsRes, productsRes] = await Promise.all([
          apiClient.get('/shipping-methods'),
          publicApi.get('/products?admin=true')
        ]);
        setProducts(productsRes.data);
        setShippingMethods(methodsRes.data);
        if (methodsRes.data.length > 0) {
          setShipping(methodsRes.data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCart(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Charger le code promo depuis localStorage au montage
  useEffect(() => {
    const savedPromo = localStorage.getItem('appliedPromo');
    if (savedPromo) {
      try {
        const promoData = JSON.parse(savedPromo);
        setPromoApplied(promoData);
        setPromoCode(promoData.code);
      } catch (err) {
        localStorage.removeItem('appliedPromo');
      }
    }
  }, []);

  const remove = async (id) => {
    try {
      if (isAuthenticated) {
        await apiClient.delete(`/cart/${id}`);
        window.dispatchEvent(new Event('cart-updated'));
      } else {
        cartStorage.removeItem(id);
        window.dispatchEvent(new Event('cart-updated'));
      }
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success(t('cartPage.removed'));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateQty = async (id, qty) => {
    if (qty < 1) return;
    try {
      if (isAuthenticated) {
        await apiClient.put(`/cart/${id}`, { quantity: qty });
        window.dispatchEvent(new Event('cart-updated'));
      } else {
        cartStorage.updateQuantity(id, qty);
        window.dispatchEvent(new Event('cart-updated'));
      }
      setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await apiClient.post(`/promo-codes/validate?code=${code}&subtotal=${subtotalTTC}`);
      const promoData = { 
        code: data.code, 
        discount: data.discount,
        discount_type: data.discount_type,
        discount_amount: data.discount_amount
      };
      setPromoApplied(promoData);
      // Sauvegarder dans localStorage pour persister entre Cart et Checkout
      localStorage.setItem('appliedPromo', JSON.stringify(promoData));
      setPromoError("");
      toast.success(`Code ${data.code} appliqué !`);
    } catch (err) {
      setPromoApplied(null);
      localStorage.removeItem('appliedPromo');
      setPromoError(formatApiError(err));
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
    setPromoError("");
    localStorage.removeItem('appliedPromo');
    toast.success(t('cartPage.promoRemoved'));
  };

  // Get cart items with product details and correct pricing
  // For PRO users: item.price is HT (shown "HT" in line items).
  // For PUBLIC users: item.price is TTC.
  const getCartItemsWithDetails = () => {
    return items.map(item => {
      const product = products.find(p => p.id === item.product_id);
      let price = item.price;
      if (!price && product) {
        const basePrice = (isPro && product.pro_price) ? product.pro_price : product.price;
        const lensModifier = item.lens_price_modifier || 0;
        price = basePrice + lensModifier;
      }
      return {
        ...item,
        product,
        displayPrice: price,
        total: price * (item.quantity || 1)
      };
    });
  };

  const cartItemsWithDetails = getCartItemsWithDetails();
  // Build the list of authorized countries (union across all methods), plus "Autre" at the end
  // France is pinned first when it is in the list, then the rest sorted alphabetically (fr locale).
  const authorizedCountries = (() => {
    const all = Array.from(new Set(
      shippingMethods.flatMap(m => Array.isArray(m.countries) ? m.countries : [])
        .map(c => String(c).trim())
        .filter(Boolean)
    ));
    const franceKey = all.find(c => c.toLowerCase() === 'france');
    const rest = all.filter(c => c !== franceKey).sort((a, b) => a.localeCompare(b, 'fr'));
    return franceKey ? [franceKey, ...rest] : rest;
  })();
  // Filter methods by selected country (methods without a country list are NOT shown — admin must explicitly allow countries)
  const methodsForCountry = selectedCountry && selectedCountry !== '__other__'
    ? shippingMethods.filter(m => Array.isArray(m.countries) && m.countries.some(c => c.toLowerCase() === selectedCountry.toLowerCase()))
    : [];
  const isCountryUnsupported = selectedCountry === '__other__';
  const selectedMethod = methodsForCountry.find(m => m.id === shipping);
  
  // Subtotal : HT pour pro, TTC pour public (selon comment les prix sont stockés/affichés)
  const subtotalRaw = cartItemsWithDetails.reduce((sum, i) => sum + i.total, 0);
  const totalQty = cartItemsWithDetails.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  // Helper: prix de livraison effectif (gratuit si seuil free_above OU free_above_qty atteint)
  const effectiveShippingPrice = (method, subtotal, qty) => {
    if (!method) return 0;
    const base = Number(method.price_ttc) || 0;
    const threshold = method.free_above;
    if (threshold != null && Number(threshold) > 0 && Number(subtotal) >= Number(threshold)) {
      return 0;
    }
    const qtyThreshold = method.free_above_qty;
    if (qtyThreshold != null && Number(qtyThreshold) > 0 && Number(qty) >= Number(qtyThreshold)) {
      return 0;
    }
    return base;
  };
  // Pour le seuil free_above on raisonne sur le TTC produits (comparable au seuil admin TTC)
  const subtotalTTCForShipping = isPro ? subtotalRaw * 1.20 : subtotalRaw;
  const shippingCostTTC = effectiveShippingPrice(selectedMethod, subtotalTTCForShipping, totalQty);
  
  // Recalculer le montant de réduction en fonction du subtotal actuel
  const calculateDiscountAmount = () => {
    if (!promoApplied) return 0;
    // La promo s'applique sur le subtotal TTC équivalent (cohérent admin)
    const base = subtotalTTCForShipping;
    if (promoApplied.discount_type === 'fixed') {
      return promoApplied.discount;
    } else if (promoApplied.discount_type === 'percentage') {
      return (base * promoApplied.discount) / 100;
    }
    return 0;
  };
  
  const discountAmount = calculateDiscountAmount();
  // Pro : Sous-total HT → + TVA → + livraison TTC → Total TTC
  // Public : Sous-total TTC (TVA incluse) + livraison TTC → Total TTC
  const subtotalHT = isPro ? subtotalRaw : +(subtotalRaw / 1.20).toFixed(2);
  const subtotalTTC = isPro ? +(subtotalRaw * 1.20).toFixed(2) : subtotalRaw;
  const tvaAmount = +(subtotalTTC - subtotalHT).toFixed(2);
  const totalTTC = +(subtotalTTC + shippingCostTTC - discountAmount).toFixed(2);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 px-4 md:px-6 py-6 md:py-10" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/collection" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-6 md:mb-8 transition-colors">
          <ArrowLeft size={16} /> {t('cartPage.continueShopping')}
        </Link>

        <div className="flex items-center gap-3 mb-10">
          <ShoppingBag size={24} className="text-[#00aaff]" />
          <h1 className="text-3xl font-black">{t("cartPage.title")}</h1>
          <span className="text-gray-500 text-sm">({items.length} article{items.length !== 1 ? "s" : ""})</span>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-24">
            <ShoppingBag size={56} className="mx-auto mb-5 text-gray-300" />
            <p className="text-gray-400 mb-6">{t("cartPage.empty")}</p>
            <Link to="/collection" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white bg-[#00aaff] hover:bg-[#0088dd] transition-colors">
              {t('cartPage.discoverCollection')} <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
            {/* Items - 3 colonnes sur 5 */}
            <div className="lg:col-span-3 space-y-3 md:space-y-4">
              {cartItemsWithDetails.map(item => {
                // Déterminer l'image à afficher - priorité à l'image de la couleur sélectionnée
                let displayImage = item.product_image;
                if (!displayImage && item.color && item.product?.color_images?.[item.color]?.length > 0) {
                  displayImage = item.product.color_images[item.color][0];
                }
                if (!displayImage && item.product?.images?.[0]) {
                  displayImage = item.product.images[0];
                }
                
                return (
                <div key={item.id} className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 flex gap-3 md:gap-4 shadow-sm" data-testid={`cart-item-${item.id}`}>
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                    {displayImage ? (
                      <img src={displayImage} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <ShoppingBag size={32} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{item.product_name}</p>
                    {item.color && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('cartPage.color')} : {typeof item.color === 'string' ? item.color : item.color?.name || item.color}
                      </p>
                    )}
                    {item.lens_option && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('cartPage.lensOption')} : {item.lens_option}
                      </p>
                    )}
                    <div className="flex items-baseline gap-2 mt-1">
                      <p className="text-[#00aaff] font-bold">
                        {item.displayPrice}€
                        {isPro && <span className="text-xs text-amber-700 font-semibold ml-1">HT</span>}
                      </p>
                      {isPro && item.product?.pro_price && item.product.pro_price < item.product.price && (
                        <span className="text-xs text-gray-400 line-through">{item.product.price}€</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
                        <button onClick={() => updateQty(item.id, (item.quantity || 1) - 1)} className="text-gray-400 hover:text-gray-900 font-bold">−</button>
                        <span className="text-sm font-medium w-4 text-center text-gray-900">{item.quantity || 1}</span>
                        <button onClick={() => updateQty(item.id, (item.quantity || 1) + 1)} className="text-gray-400 hover:text-gray-900 font-bold">+</button>
                      </div>
                      <button onClick={() => remove(item.id)} className="text-gray-400 hover:text-red-400 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-black">
                      {item.total.toFixed(2)}€
                      {isPro && <span className="text-[10px] text-amber-700 font-semibold ml-1">HT</span>}
                    </p>
                  </div>
                </div>
              );})}
            </div>

            {/* Summary - 2 colonnes sur 5 pour plus de largeur */}
            <div className="lg:col-span-2">
              {/* Promo code */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('cartPage.promoCode')}</p>
                {promoApplied ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Check size={14} className="text-green-500" />
                      <span className="text-sm font-bold text-green-700">{promoApplied.code}</span>
                      <span className="text-xs text-green-600">
                        {promoApplied.discount_type === 'fixed' ? `-${promoApplied.discount}€` : `-${promoApplied.discount}%`}
                      </span>
                    </div>
                    <button onClick={removePromo} className="text-gray-400 hover:text-red-400" title="Retirer le code">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input 
                      value={promoCode} 
                      onChange={e => { setPromoCode(e.target.value); setPromoError(""); }}
                      onKeyDown={e => e.key === "Enter" && applyPromo()}
                      placeholder="Ex: WELCOME10"
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#00aaff] bg-white transition-colors" 
                    />
                    <button onClick={applyPromo} className="bg-[#00aaff] text-white px-4 py-2.5 rounded-xl text-xs font-bold hover:bg-[#0088dd] transition-colors whitespace-nowrap">
                      Appliquer
                    </button>
                  </div>
                )}
                {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
              </div>

              <div className="bg-white border border-gray-100 rounded-2xl p-6 sticky top-24 shadow-sm">
                <h2 className="font-bold text-lg mb-5 text-gray-900">{t("cartPage.summary")}</h2>

                {/* Shipping options */}
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Livraison pour le pays</p>
                  <select
                    data-testid="shipping-country-select"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-3 outline-none focus:border-[#00aaff] bg-white"
                  >
                    <option value="">— Sélectionnez votre pays —</option>
                    {authorizedCountries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__other__">Autre</option>
                  </select>

                  {isCountryUnsupported ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="country-unsupported-message">
                      Ce pays n'est pas pris en charge pour le moment, merci de nous contacter pour en savoir plus sur <a href="mailto:contact@joeycyan.com" className="font-semibold underline hover:text-amber-700">contact@joeycyan.com</a>
                    </div>
                  ) : !selectedCountry ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
                      Sélectionnez d'abord votre pays pour afficher les modes de livraison disponibles.
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('cartPage.shippingMethod')}</p>
                      {methodsForCountry.length === 0 ? (
                        <p className="text-xs text-gray-400">{t('cartPage.noShipping')}</p>
                      ) : (
                        <div className="space-y-2">
                          {methodsForCountry.map(m => {
                            const methodEffectivePrice = effectiveShippingPrice(m, subtotalTTC, totalQty);
                            const isFreeByAmount = m.free_above != null && Number(m.free_above) > 0 && Number(m.price_ttc) > 0 && subtotalTTC >= Number(m.free_above);
                            const isFreeByQty = m.free_above_qty != null && Number(m.free_above_qty) > 0 && Number(m.price_ttc) > 0 && totalQty >= Number(m.free_above_qty);
                            const missingForFree = m.free_above != null && Number(m.free_above) > 0 && Number(m.price_ttc) > 0 && subtotalTTC < Number(m.free_above)
                              ? Number(m.free_above) - subtotalTTC
                              : 0;
                            const missingQtyForFree = m.free_above_qty != null && Number(m.free_above_qty) > 0 && Number(m.price_ttc) > 0 && totalQty < Number(m.free_above_qty)
                              ? Number(m.free_above_qty) - totalQty
                              : 0;
                            return (
                              <label key={m.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${shipping === m.id ? "border-[#00aaff] bg-[#e0f4ff]/40" : "border-gray-200 hover:border-gray-300"}`}>
                                <input type="radio" name="shipping" value={m.id} checked={shipping === m.id} onChange={() => setShipping(m.id)} className="mt-0.5 accent-[#00aaff]" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                                  {m.description && <p className="text-xs text-gray-400">{m.description}</p>}
                                  {m.estimated_days && <p className="text-xs text-gray-400">{m.estimated_days}</p>}
                                  {isFreeByAmount && (
                                    <p className="text-xs text-[#00aaff] font-semibold mt-0.5">Offerte (commande ≥ {Number(m.free_above).toFixed(2)}€)</p>
                                  )}
                                  {isFreeByQty && !isFreeByAmount && (
                                    <p className="text-xs text-[#00aaff] font-semibold mt-0.5">Offerte (≥ {m.free_above_qty} article{m.free_above_qty > 1 ? 's' : ''})</p>
                                  )}
                                  {!isFreeByAmount && !isFreeByQty && missingForFree > 0 && (
                                    <p className="text-xs text-gray-500 mt-0.5">Plus que <span className="font-semibold text-[#00aaff]">{missingForFree.toFixed(2)}€</span> pour la livraison offerte</p>
                                  )}
                                  {!isFreeByAmount && !isFreeByQty && missingQtyForFree > 0 && (
                                    <p className="text-xs text-gray-500 mt-0.5">Ou encore <span className="font-semibold text-[#00aaff]">{missingQtyForFree} article{missingQtyForFree > 1 ? 's' : ''}</span> pour la livraison offerte</p>
                                  )}
                                </div>
                                <span className={`text-sm font-bold shrink-0 ${methodEffectivePrice === 0 ? "text-[#00aaff]" : "text-gray-700"}`}>
                                  {methodEffectivePrice === 0
                                    ? "Gratuit"
                                    : `+${methodEffectivePrice.toFixed(2)}€`}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Totals */}
                <div className="space-y-3 text-sm mb-5">
                  <div className="flex justify-between text-gray-400">
                    <span>{isPro ? 'Sous-total HT' : t('cartPage.subtotalTTC')}</span>
                    <span>{subtotalHT.toFixed(2)}€{isPro && ' HT'}</span>
                  </div>
                  {isPro && (
                    <div className="flex justify-between text-gray-400">
                      <span>TVA (20%)</span>
                      <span>{tvaAmount.toFixed(2)}€</span>
                    </div>
                  )}
                  {promoApplied && (
                    <div className="flex justify-between text-green-600 font-semibold">
                      <span>
                        Réduction {promoApplied.discount_type === 'fixed' ? `${promoApplied.discount}€` : `${promoApplied.discount}%`}
                      </span>
                      <span>-{discountAmount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-400">
                    <span>{t("cartPage.shippingTTC")}</span>
                    <span className={shippingCostTTC === 0 ? "text-[#00aaff]" : "text-gray-700"}>
                      {shippingCostTTC === 0 ? t("cartPage.freeShipping") : `${shippingCostTTC.toFixed(2)}€`}
                    </span>
                  </div>
                  {!isPro && (
                    <div className="flex justify-between text-gray-400 text-xs">
                      <span>{t('cartPage.inclVAT')}</span>
                      <span>{tvaAmount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-base">
                    <span>{t('cartPage.totalTTC')}</span>
                    <span className="text-[#00aaff] text-xl">{totalTTC.toFixed(2)}€</span>
                  </div>
                </div>

                <button 
                  onClick={() => navigate('/checkout')}
                  disabled={!selectedCountry || isCountryUnsupported || !shipping}
                  className={`w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-3 transition-colors mb-4 ${(!selectedCountry || isCountryUnsupported || !shipping) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                  data-testid="checkout-btn"
                >
                  <ArrowRight size={16} />
                  {t('cartPage.checkout')}
                </button>

                {/* Security badges */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-center gap-1 mb-3">
                    <Lock size={11} className="text-green-500" />
                    <p className="text-[11px] text-gray-400 font-medium">{t('cartPage.securePayment')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
